/**
 * BridgeTopBar — the bridge's fixed 44px chrome bar (Wave 2, Item 2).
 * Mounted by BridgeShell inside its 48px header frame; owns ALL top-bar
 * chrome (the item-1 temporary inline bar in FleetHud is gone — one
 * relocation, zero duplication).
 *
 * CONSUMERS:
 * - frontend/src/components/bridge/BridgeShell.tsx (only)
 *
 * Four regions, left → right:
 *   1. Identity   — 'FORGE//FLEET', DM Mono tracked caps (never display font)
 *   2. Mood pill  — label + glow/border from MOOD_STYLE[mood]; the pulsing
 *                   dot is in the DOM ONLY while a run is active (absent, not
 *                   merely non-pulsing, when idle)
 *   3. Countdown  — 'next refresh Ns' recomputed off useNow every tick,
 *                   clamped at 0; '—' until the first poll lands
 *   4. Controls   — BRIDGE/COMPACT segmented toggle + ⌂ CONSOLE exit
 *
 * Chrome rules (same as BridgeShell): rounded-sm only, hairline
 * border-white/[0.06], near-black + #FF6600 + DM Mono. No transition-* // design-law-allow
 * classes: BridgeShell's structural test asserts the shell subtree is
 * animation-free when no run is active, and this bar renders inside it.
 */
import React, { useState } from 'react';
import { MOOD_STYLE, useNow, type FleetMood } from '../fleet/fleetMood';
import { ACCENT } from '../fleet/ReactorCore';
import { getSoundEnabled, setSoundEnabled } from './bridgeSound';

interface BridgeTopBarProps {
  mood: FleetMood;
  /** ms epoch of the last successful poll; null until the first poll lands. */
  polledAt: number | null;
  pollMs: number;
  /** True only while the latest run is in an ACTIVE_STATUSES state. */
  isRunActive: boolean;
  compact: boolean;
  onToggleCompact: () => void;
  onExitToConsole: () => void;
}

export default function BridgeTopBar({
  mood,
  polledAt,
  pollMs,
  isRunActive,
  compact,
  onToggleCompact,
  onExitToConsole,
}: BridgeTopBarProps) {
  // The honest heartbeat — ticks reactively; never a wall-clock Date.now()
  // inline in render (that would go stale between parent renders).
  const now = useNow();
  const style = MOOD_STYLE[mood];

  // Wave-3 item 3: event-sound toggle. State mirrors the persisted pref so
  // the click re-renders with the new value (no stale closure read).
  const [soundOn, setSoundOn] = useState<boolean>(() => getSoundEnabled());

  const secondsLeft =
    polledAt === null ? null : Math.max(0, Math.ceil((polledAt + pollMs - now) / 1000));

  const subModeBtn = (active: boolean) =>
    `rounded-sm border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
      active ? `border-[${ACCENT}] text-[${ACCENT}]` : 'border-white/[0.06] text-text-muted'
    }`;

  return (
    <div className="flex h-11 w-full items-center gap-4 px-4">
      {/* 1 · IDENTITY — DM Mono only, tracked caps, regular weight. */}
      <span className="font-mono text-[11px] font-normal uppercase tracking-[0.3em] text-text-primary">
        FORGE//FLEET
      </span>

      {/* 2 · MOOD PILL — label + glow straight from MOOD_STYLE; the pulse dot
              exists in the DOM only while a run is active. */}
      <span
        data-testid="mood-pill"
        className="flex items-center gap-2 rounded-sm border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-text-primary"
        style={{ borderColor: style.glow, boxShadow: `0 0 8px ${style.glow}` }}
      >
        {isRunActive && (
          <span
            data-testid="mood-pulse-dot"
            aria-hidden="true"
            className={`h-1.5 w-1.5 animate-pulse rounded-full bg-[${ACCENT}]`}
            style={
              style.pulseSeconds > 0
                ? { animationDuration: `${style.pulseSeconds}s` }
                : undefined
            }
          />
        )}
        {style.label}
      </span>

      {/* 3 · POLL COUNTDOWN — clamped at 0; '—' before the first poll. */}
      <span
        data-testid="poll-countdown"
        className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted"
      >
        next refresh {secondsLeft === null ? '—' : `${secondsLeft}s`}
      </span>

      {/* 4 · CONTROLS — BRIDGE/COMPACT sub-mode + ⌂ CONSOLE exit. Clicking the
              already-active sub-mode is a no-op (no redundant write). */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-pressed={!compact}
          className={subModeBtn(!compact)}
          onClick={() => {
            if (compact) onToggleCompact();
          }}
        >
          Bridge
        </button>
        <button
          type="button"
          aria-pressed={compact}
          className={subModeBtn(compact)}
          onClick={() => {
            if (!compact) onToggleCompact();
          }}
        >
          Compact
        </button>
        <button
          type="button"
          className="rounded-sm border border-white/[0.06] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted"
          onClick={onExitToConsole}
        >
          ⌂ Console
        </button>
        {/* Wave-3 item 3: event-sound toggle (default OFF). aria-pressed
            binds to the pref-mirroring state; brand colour when ON, muted
            when OFF — same token grammar as the sub-mode buttons. */}
        <button
          type="button"
          aria-pressed={soundOn}
          aria-label="Toggle event sound"
          title={soundOn ? 'Event sound on' : 'Event sound off'}
          className={`rounded-sm border px-2 py-1 font-mono text-[10px] ${
            soundOn ? 'border-brand text-brand' : 'border-white/[0.06] text-text-muted'
          }`}
          onClick={() => {
            const next = !soundOn;
            setSoundEnabled(next);
            setSoundOn(next);
          }}
        >
          ♪
        </button>
      </div>
    </div>
  );
}
